import { ButtonBase, Modal } from '@mui/material';
import { useRouter } from 'next/router';

import Button, { ButtonVariant } from '@/components/BaseLibrary/Button/Button';
import Icon, { IconName } from '@/components/Icon/Icon';
import ArtDirectedImage, {
  ImageSources,
} from '@/components/Image/ArtDirectedImage';
import RichText from '@/components/RichText/RichText';
import { FrontifyImageContent } from '@/contentful';
import TextLogo from '@/public/cmsAssets/assets/img/logo/TextLogo';
import { brandColors } from '@/styles/sunrunTheme.styles';
import { ActionModalCF } from '@/types';

import useStyles from './ActionModal.styles';

const getImages = (
  images: FrontifyImageContent[],
  isHorizontal: boolean
): ImageSources => {
  if (images.length === 2 && isHorizontal) {
    return {
      md: [images[1], 600],
      xs: [images[0], 300],
      fallback: [images[0], 300],
    };
  }

  return {
    md: [images[0], 600],
    xs: [images[0], 300],
    fallback: [images[0], 300],
  };
};

interface ActionModalProps {
  open: boolean;
  onClose: () => void;
  modalContent: ActionModalCF;
}

export enum ModalOrientation {
  HORIZONTAL = 'Horizontal',
  VERTICAL = 'Vertical',
}

export enum HeadingSize {
  SM = 'SM',
  LG = 'LG',
}

export default function ActionModal({
  open,
  onClose,
  modalContent,
}: ActionModalProps) {
  const {
    showLogo,
    orientation,
    images,
    heading,
    headingSize,
    subheading,
    termsAndConditions,
    cta,
    secondaryCtaLabel,
    closeButtonStyle,
  } = modalContent.fields;
  const isHorizontal = orientation === ModalOrientation.HORIZONTAL;
  const router = useRouter();

  const { classes, cx } = useStyles({ isHorizontal, closeButtonStyle });

  const onCtaClick = () => {
    onClose();
    if (cta?.fields.url) router.push(cta.fields.url);
  };
  const imageSources = getImages(
    images as unknown as FrontifyImageContent[],
    isHorizontal
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby='modal-title'
      aria-describedby='modal-description'
    >
      <div
        className={cx(classes.content, {
          [classes.horizontalContent]: isHorizontal,
        })}
      >
        <ButtonBase
          className={classes.closeButton}
          disableRipple
          aria-label='close modal'
          onClick={onClose}
        >
          <Icon
            name={IconName.CLOSE}
            width={24}
            className={classes.closeIcon}
          />
        </ButtonBase>

        <div className={classes.imageContainer}>
          {images?.length && (
            <ArtDirectedImage
              images={imageSources}
              alt=''
              role='presentation'
              pictureProps={{ className: classes.image }}
            />
          )}
        </div>

        <div className={classes.contentContainer}>
          {showLogo && (
            <div>
              <TextLogo width='75' color={brandColors.primaryMain} />
            </div>
          )}

          <div className={classes.verticalGapZero}>
            {heading && (
              <h2
                className={cx(classes.heading, {
                  [classes.headingSm]: headingSize === HeadingSize.SM,
                })}
                id='modal-title'
              >
                {heading}
              </h2>
            )}
            {subheading && (
              <RichText
                content={subheading}
                className={classes.subheading}
                textId='modal-description'
              />
            )}
          </div>

          <div className={classes.verticalGapZero}>
            {cta && (
              <Button
                href={cta.fields.url}
                label={cta.fields.text}
                className={classes.cta}
                onClick={onCtaClick}
              />
            )}
            {secondaryCtaLabel && (
              <Button
                label={secondaryCtaLabel}
                variant={ButtonVariant.TERTIARY}
                className={classes.secondaryCTA}
                onClick={onClose}
              />
            )}
          </div>

          {termsAndConditions && (
            <RichText
              content={termsAndConditions}
              className={classes.termsAndConditions}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
